import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";

const ENGAGEMENTS = [
  {
    code: "01",
    title: "Custom Plugin & Addon Development",
    tag: "FIXED-SCOPE BUILD",
    to: "/contact?type=plugin",
    testid: "engagement-plugin",
    body: "A defined system, built and delivered. Oxide plugins for Rust, native addons for World of Warcraft, and game tooling built to your exact specification.",
    includes: [
      "Scoped specification before a line of code",
      "Built against your server or client environment",
      "Hardened on live infrastructure, not a test bench",
      "Documentation, permissions and config handover",
    ],
    examples: "Statistics engines · in-game interfaces · identity & linking · balance tooling",
  },
  {
    code: "02",
    title: "Server Operations & Admin Apps",
    tag: "INFRASTRUCTURE BUILD",
    to: "/contact?type=ops",
    testid: "engagement-ops",
    body: "The machinery behind a serious network. Telemetry bridges, release pipelines, admin dashboards and management tooling for communities that can't afford downtime.",
    includes: [
      "Live server telemetry and health monitoring",
      "Release pipelines with validation and rollback",
      "Admin surfaces your staff will actually use",
      "Network-wide deployment without config wipeouts",
    ],
    examples: "Telemetry bridges · release management · staff tooling · network dashboards",
  },
  {
    code: "03",
    title: "Live Ops Retainer",
    tag: "ONGOING PARTNERSHIP",
    to: "/contact?type=retainer",
    testid: "engagement-retainer",
    body: "The forge stays hot. Ongoing maintenance, updates and balance passes for systems we built — or systems you already run that need a steadier hand.",
    includes: [
      "Scheduled update and balance cycles",
      "Incident response within agreed windows",
      "Seasonal and wipe-cycle preparation",
      "Priority channel direct to the studio",
    ],
    examples: "Season prep · balance passes · version upgrades · incident response",
  },
];

const PROCESS = [
  { step: "BRIEF", body: "You tell us the problem. We ask uncomfortable questions until the scope is real." },
  { step: "SCOPE", body: "Fixed deliverables, fixed timeline, fixed price. No ambiguity survives this stage." },
  { step: "BUILD", body: "Development with working builds along the way — you see progress, not promises." },
  { step: "HARDEN", body: "Tested under live conditions. Load, abuse and edge cases before handover." },
  { step: "HANDOVER", body: "Code, config, documentation and a walkthrough. Optional retainer from here." },
];

const PROOF = [
  ["5", "SERVER LIVE NETWORK"],
  ["8+", "PROPRIETARY SYSTEMS"],
  ["0", "MISSED WIPE DEADLINES"],
  ["24/7", "PRODUCTION UPTIME FOCUS"],
];

const Services = () => (
  <div className="relative pt-32">
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
      style={{
        background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
      }}
    />
    <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">SERVICES // COMMISSION THE FORGE</p>
        <h1 className="mt-5 max-w-4xl font-display text-5xl font-black uppercase leading-[0.95] tracking-tight text-[#F4F5F7] sm:text-7xl lg:text-8xl">
          Built for your world
        </h1>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#9EA4B0] sm:text-lg">
          Everything we ship for ourselves — plugins, addons, admin applications and live operations
          infrastructure — is available as commissioned work. Every system we offer has already
          survived production on the Arsenal Servers network: five live Rust servers, a Gold Master
          release pipeline, and a community that notices when something breaks.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-px border border-[#282D37] bg-[#282D37] sm:max-w-2xl lg:grid-cols-4">
          {PROOF.map(([v, k], i) => (
            <div key={k} data-testid={`services-proof-${i}`} className="bg-[#0D0F12] px-4 py-5 text-center">
              <p className="font-display text-2xl font-black text-[#E52E2E] sm:text-3xl">{v}</p>
              <p className="mt-1 font-mono text-[8px] tracking-[0.25em] text-[#9EA4B0]">{k}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </header>

    <section data-testid="engagements" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">ENGAGEMENT TYPES</p>
        <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
          Three ways to put the forge to work
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {ENGAGEMENTS.map((e, i) => (
          <Reveal key={e.code} delay={0.1 * i}>
            <div
              data-testid={e.testid}
              className="group steel-panel relative flex h-full flex-col p-8 transition-[border-color,box-shadow] duration-500 hover:border-[#E52E2E]/50 hover:shadow-[0_0_40px_rgba(229,46,46,0.08)]"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm tracking-[0.3em] text-[#5F6573] transition-colors duration-500 group-hover:text-[#E52E2E]">
                  {e.code}
                </span>
                <span className="border border-[#282D37] px-2.5 py-1 font-mono text-[9px] tracking-[0.25em] text-[#9EA4B0]">
                  {e.tag}
                </span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold uppercase leading-tight tracking-wide text-[#F4F5F7]">
                {e.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">{e.body}</p>
              <ul className="mt-6 flex-1 space-y-3 border-t border-[#282D37] pt-6">
                {e.includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 text-sm text-[#9EA4B0]">
                    <Check size={14} className="mt-0.5 shrink-0 text-[#E52E2E]" />
                    {inc}
                  </li>
                ))}
              </ul>
              <p className="mt-6 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-[#5F6573]">
                {e.examples.toUpperCase()}
              </p>
              <Link
                to={e.to}
                data-testid={`${e.testid}-cta`}
                className="group/btn mt-8 inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 hover:text-[#E52E2E]"
              >
                START THIS CONVERSATION
                <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
              </Link>
              <span className="absolute bottom-0 left-0 h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="services-process" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
      <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">HOW ENGAGEMENTS RUN</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            Brief to handover
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px border border-[#282D37] bg-[#282D37] sm:grid-cols-2 lg:grid-cols-5">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={0.07 * i}>
              <div data-testid={`process-${i}`} className="h-full bg-[#0D0F12] p-7">
                <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#E52E2E]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-[#F4F5F7]">{p.step}</p>
                <p className="mt-3 text-sm leading-relaxed text-[#9EA4B0]">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-10 max-w-2xl text-sm leading-relaxed text-[#5F6573]">
            A note on fit: we take on a small number of commissions at a time, and we say no when a
            project doesn't suit the forge. If we take yours, it gets the same standard we hold
            Territoria and the Arsenal network to.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:gap-8">
            <Link
              to="/case-studies/arsenalwebstats"
              data-testid="services-case-study-link"
              className="group inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
            >
              PROOF: THE ARSENALWEBSTATS CASE STUDY
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="relative mx-auto max-w-[1400px] overflow-hidden px-6 py-24 lg:px-10">
      <EmberCanvas density={12} className="pointer-events-none absolute inset-0 h-full w-full opacity-30" />
      <Reveal>
        <div className="relative border border-[#282D37] bg-[#0D0F12] p-10 text-center sm:p-16">
          <p className="font-display text-3xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
            Have something that <span className="text-[#E52E2E]">needs forging?</span>
          </p>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-[#9EA4B0] sm:text-base">
            Send a brief. You'll get a straight answer on fit, scope and timeline — not a sales funnel.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="mt-8 inline-block bg-[#E52E2E] px-9 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_28px_rgba(229,46,46,0.25)] transition-colors duration-300 hover:bg-[#FF3B3B]"
          >
            Start a Project
          </Link>
        </div>
      </Reveal>
    </section>
  </div>
);

export default Services;
